import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { DeleteConfirmDialog } from '~/components/common/DeleteConfirmDialog'
import {
  convertListBusinessMastersResponse,
  useListBusinessMasters,
} from '~/hooks/businessMaster/useListBusinessMasters'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { useMasterInlineDelete } from '~/pages/AdminOcctoMaster/shell/useMasterInlineDelete'
import { BgMasterTable } from '~/pages/AdminOcctoMaster/tabs/BgMaster/components/BgMasterTable'
import { CreateBgMasterModal } from '~/pages/AdminOcctoMaster/tabs/BgMaster/components/CreateBgMasterModal'
import { EditBgMasterModal } from '~/pages/AdminOcctoMaster/tabs/BgMaster/components/EditBgMasterModal'
import { useDeleteBgMaster } from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useDeleteBgMaster'
import {
  type BgMasterRow,
  convertListBgMastersResponse,
  useListBgMasters,
} from '~/pages/AdminOcctoMaster/tabs/BgMaster/hooks/useListBgMasters'
import { bgMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/labels'
import { availableLicenseTypes } from '~/pages/AdminOcctoMaster/tabs/BgMaster/models/licenseTypeOptions'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'
import { useTodayJst } from '~/pages/AdminOcctoMaster/tabs/_common/hooks/useTodayJst'

const DEFAULT_AREA: AreaCode = AreaCode.UNSPECIFIED

function filterRows(
  rows: BgMasterRow[],
  areaCode: AreaCode,
  searchText: string,
): BgMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows
    .filter((row) => {
      if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
        return false
      }
      if (trimmed === '') {
        return true
      }
      return (
        row.bgCode.toLowerCase().includes(trimmed) ||
        row.bgName.toLowerCase().includes(trimmed)
      )
    })
    .slice()
    .sort((a, b) => a.bgCode.localeCompare(b.bgCode))
}

export const BgMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListBgMasters()
  const today = useTodayJst()

  const [selectedAreaCode, setSelectedAreaCode] =
    useState<AreaCode>(DEFAULT_AREA)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? convertListBgMastersResponse(data) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  // AC-OMB-025 / AC-MOI-005 (BG タブ例外): BG区分 select の選択肢
  // (`~/pages/.../models/licenseTypeOptions` の `availableLicenseTypes` が SoT、
  // Decision 4「絞り込み結果を直接使う」) が 0 件になる時、「新規登録」ボタンを
  // 非活性化する。 Create モーダル (`BgMasterFormFields`) は同じ関数を使うため
  // 判定軸が二重管理にならない。
  //
  // 未取得 (`businessMastersData === undefined`) の間は絞り込み判定をせず
  // 既存挙動どおり活性のままにする (Open questions: ロード完了までは活性のままでも
  // AC 違反なし。0 件確定は応答後)。
  // businessMasters はここでは新規登録ボタンの活性判定の供給源にしか使わないため
  // `throwOnError: false` を渡す。 既定 (true) だと副次データの取得失敗が error
  // boundary 経由で BG 一覧 (AC-MOI-001 / AC-OMB-017) ごと全画面エラーに飛ばす。
  // 失敗時は `data === undefined` = addDisabled=false (従来挙動) へ倒す。
  const { data: businessMastersData } = useListBusinessMasters({
    throwOnError: false,
  })
  const addDisabled = useMemo(() => {
    if (businessMastersData === undefined) {
      return false
    }
    const businessMasters =
      convertListBusinessMastersResponse(businessMastersData)
    return availableLicenseTypes(businessMasters, today).length === 0
  }, [businessMastersData, today])

  const subject = t('page:adminOcctoMaster.common.subject.bg')
  const tab = useMasterCrudTab<BgMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix: 'page:adminOcctoMaster.bgMaster.serverError',
    filteredRows,
    csv: { columns: bgMasterCsvColumns(), fileBase: 'bg' },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })
  const { handleDeleteConfirm, isDeleting } =
    useMasterInlineDelete<BgMasterRow>({
      modalState: tab.modalState,
      closeModal: tab.closeModal,
      setDeleteError: tab.setDeleteError,
      errorKeyPrefix: 'page:adminOcctoMaster.bgMaster.serverError',
      useDelete: useDeleteBgMaster,
    })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      addDisabled={addDisabled}
      addDisabledReason={t(
        'page:adminOcctoMaster.bgMaster.toolbar.addDisabledReason',
      )}
      createModal={
        tab.modalState.kind === 'create' ? (
          <CreateBgMasterModal
            isOpen={tab.modalState.kind === 'create'}
            onClose={tab.closeModal}
          />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteConfirmDialog
            entityName={t('page:adminOcctoMaster.bgMaster.deleteEntityName', {
              code: deleteRow.bgCode,
              name: deleteRow.bgName,
            })}
            errorIsRetriable={tab.deleteError?.retriable}
            errorMessage={tab.deleteError?.message}
            isLoading={isDeleting}
            isOpen={tab.modalState.kind === 'delete'}
            onCancel={tab.closeModal}
            onConfirm={() => {
              void handleDeleteConfirm()
            }}
            title={t('page:adminOcctoMaster.bgMaster.delete.confirmTitle')}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditBgMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          expiredFilter={{
            expiredCount: tab.expiredCount,
            includeExpired: tab.includeExpired,
            onChange: tab.setIncludeExpired,
            subject,
          }}
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.bgMaster.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <BgMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
